import { makeApiError } from '@packages/shared-ui/errors';
import axios, { type AxiosResponse } from 'axios';
import {
  BATTERY_PRODUCTS_PUBLIC_URL,
  GET_MARKET_URL,
  PUBLIC_RETROFIT_PROPOSAL_URL,
  QUOTE_URL,
  RETROFIT_LEAD_URL,
  SERVICE_TERRITORY_URL,
  SYSTEM_OVERVIEWS_URL,
} from '@/constants/baseUrls';
import type { BatteryProducts } from '@/types/models/battery/battery';
import type { MarketInfo } from '@/types/models/solarEcomm/marketInfo';
import type { SystemOverviewSummary } from '@/types/models/solarEcomm/systemOverview';
import type {
  MarketRequest,
  PublicRetrofitProposalPayload,
  ServiceTerritoryRequest,
  ServiceTerritoryResponse,
  SubmitQuoteRequest,
} from './pxeBackendClient.types';

export type { PublicRetrofitProposalPayload } from './pxeBackendClient.types';

export interface BatteryProductsPublicQuery {
  state: string;
  utility: string;
}

export const fetchPublicBatteryProducts = async (
  query: BatteryProductsPublicQuery
): Promise<BatteryProducts> => {
  try {
    const response = await axios.get<BatteryProducts>(
      BATTERY_PRODUCTS_PUBLIC_URL,
      { params: query }
    );
    return response.data;
  } catch (err) {
    console.error(err);
    throw makeApiError(err);
  }
};

export interface RetrofitLeadPayload {
  prospectId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  zip: string;
  leadOrigin: 'web';
}

export const createRetrofitLead = async (
  retrofitLeadPayload: RetrofitLeadPayload,
  accessToken: string
) => {
  try {
    const response = await axios.post(RETROFIT_LEAD_URL, retrofitLeadPayload, {
      headers: { Authorization: accessToken },
    });
    return response.data;
  } catch (err) {
    console.error(err);
    throw makeApiError(err);
  }
};

export const createPublicRetrofitProposal = async (
  payload: PublicRetrofitProposalPayload
) => {
  try {
    const response = await axios.post(PUBLIC_RETROFIT_PROPOSAL_URL, payload);
    return response.data;
  } catch (err) {
    console.error(err);
    throw makeApiError(err);
  }
};

export const fetchSystemOverview = async (
  prospectId: string,
  accessToken: string
): Promise<SystemOverviewSummary> => {
  try {
    const response = await axios.get<SystemOverviewSummary>(
      SYSTEM_OVERVIEWS_URL,
      {
        params: { prospectId },
        headers: { Authorization: accessToken },
      }
    );
    return response.data;
  } catch (err) {
    console.error(err);
    throw makeApiError(err);
  }
};

export const submitQuote = async (body: SubmitQuoteRequest) => {
  try {
    const response = await axios.post(QUOTE_URL, body);
    return response.data;
  } catch (err) {
    console.error(err);
    throw makeApiError(err);
  }
};

export const getMarketInfo = async (
  query: MarketRequest
): Promise<AxiosResponse<MarketInfo>> => {
  try {
    return await axios.get<MarketInfo>(GET_MARKET_URL, {
      params: query,
    });
  } catch (err) {
    console.error(err);
    throw makeApiError(err);
  }
};

export const postServiceTerritory = async (
  body: ServiceTerritoryRequest
): Promise<ServiceTerritoryResponse> => {
  try {
    const response = await axios.post<ServiceTerritoryResponse>(
      SERVICE_TERRITORY_URL,
      body
    );
    return response.data;
  } catch (err) {
    console.error(err);
    throw makeApiError(err);
  }
};
